import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLocale } from '@/contexts/LocaleContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getCompanies } from '@/lib/api';
import { 
  Search, 
  Building2, 
  MapPin, 
  CheckCircle2, 
  Star, 
  ChevronRight,
  Megaphone,
  Filter,
  CalendarCheck
} from 'lucide-react';

const ProvidersPage = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [schedulingFilter, setSchedulingFilter] = useState(false);
  const { isUSA } = useLocale();

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const data = await getCompanies({ company_type: 'proveedor', limit: 100 });
      // Sort: sponsored first, then by rating
      const sorted = data.sort((a, b) => {
        if (a.is_sponsored && !b.is_sponsored) return -1;
        if (!a.is_sponsored && b.is_sponsored) return 1;
        return (b.rating_average || 0) - (a.rating_average || 0);
      });
      setCompanies(sorted);
    } catch (error) {
      console.error('Error fetching companies:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredCompanies = companies.filter(company => {
    const matchesSearch = !searchQuery || 
      company.company_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      company.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesLocation = !locationFilter || company.location_state === locationFilter;
    const matchesCategory = !categoryFilter || company.industry === categoryFilter;
    const matchesScheduling = !schedulingFilter || company.accepts_scheduling;
    return matchesSearch && matchesLocation && matchesCategory && matchesScheduling;
  });

  // Get unique locations and categories
  const locations = [...new Set(companies.map(c => c.location_state).filter(Boolean))];
  const categories = [...new Set(companies.map(c => c.industry).filter(Boolean))];

  // Separate sponsored and regular
  const sponsoredCompanies = filteredCompanies.filter(c => c.is_sponsored);
  const regularCompanies = filteredCompanies.filter(c => !c.is_sponsored);

  return (
    <div data-testid="providers-page">
      <div className="page-header">
        <h1 className="page-title flex items-center gap-2">
          <Building2 className="w-6 h-6" style={{ color: '#1E4C8E' }} />
          {isUSA ? 'Suppliers' : 'Proveedores'}
        </h1>
        <p className="page-subtitle">
          {isUSA ? 'Find suppliers of fabrics, threads, trims and more' : 'Encuentra proveedores de telas, hilos, avíos y más'}
        </p>
      </div>

      {/* Filters */}
      <Card className="card-industrial mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder={isUSA ? 'Search by name or description...' : 'Buscar por nombre o descripción...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={locationFilter || "all"} onValueChange={(v) => setLocationFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder={isUSA ? 'Location' : 'Ubicación'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{isUSA ? 'All locations' : 'Todas las ubicaciones'}</SelectItem>
                {locations.map(loc => (
                  <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={categoryFilter || "all"} onValueChange={(v) => setCategoryFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder={isUSA ? 'Category' : 'Categoría'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{isUSA ? 'All categories' : 'Todas las categorías'}</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button
              onClick={() => setSchedulingFilter(!schedulingFilter)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                schedulingFilter 
                  ? 'bg-blue-100 border-blue-400 text-blue-700' 
                  : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
            >
              <CalendarCheck className="w-4 h-4" />
              <span className="text-sm">{isUSA ? 'With scheduling' : 'Con programación'}</span>
            </button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Sponsored Section */}
          {sponsoredCompanies.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Megaphone className="w-5 h-5 text-yellow-500" />
                <h2 className="font-semibold text-slate-700">{isUSA ? 'Featured' : 'Destacados'}</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {sponsoredCompanies.map((company) => (
                  <CompanyCard key={company.company_id} company={company} isSponsored isUSA={isUSA} />
                ))}
              </div>
            </div>
          )}

          {/* Regular Companies */}
          {regularCompanies.length > 0 && (
            <div>
              {sponsoredCompanies.length > 0 && (
                <h2 className="font-semibold text-slate-700 mb-3">{isUSA ? 'All suppliers' : 'Todos los proveedores'}</h2>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {regularCompanies.map((company) => (
                  <CompanyCard key={company.company_id} company={company} isUSA={isUSA} />
                ))}
              </div>
            </div>
          )}

          {filteredCompanies.length === 0 && (
            <div className="text-center py-12">
              <Building2 className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="font-semibold text-slate-700 mb-2">{isUSA ? 'No suppliers found' : 'No se encontraron proveedores'}</h3>
              <p className="text-slate-500 text-sm">{isUSA ? 'Try different search filters' : 'Intenta con otros filtros de búsqueda'}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const CompanyCard = ({ company, isSponsored, isUSA }) => (
  <Link to={`/company/${company.company_id}`}>
    <Card className={`card-industrial hover:shadow-md transition-shadow cursor-pointer ${isSponsored ? 'border-yellow-400 border-2 bg-yellow-50/30' : ''} ${company.is_featured && !isSponsored ? 'border-emerald-400 border bg-emerald-50/30' : ''}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          {company.logo_url ? (
            <img src={company.logo_url} alt={company.company_name} className="w-14 h-14 rounded-lg object-cover" />
          ) : (
            <div className="w-14 h-14 rounded-lg bg-slate-100 flex items-center justify-center">
              <Building2 className="w-6 h-6 text-slate-400" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-slate-900 truncate">{company.company_name}</h3>
              {company.verified && (
                <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
              )}
              {isSponsored && (
                <Badge className="bg-yellow-500 text-white text-xs">{isUSA ? 'Sponsored' : 'Patrocinado'}</Badge>
              )}
              {company.is_featured && !isSponsored && (
                <Badge className="bg-emerald-500 text-white text-xs">{isUSA ? 'Featured' : 'Destacado'}</Badge>
              )}
            </div>
            <p className="text-sm text-slate-500 line-clamp-2 mt-1">{company.description || company.industry}</p>
            <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {company.location_city}, {company.location_state}
              </span>
              {company.rating_average > 0 && (
                <span className="flex items-center gap-1">
                  <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                  {company.rating_average.toFixed(1)}
                </span>
              )}
              {company.accepts_scheduling && (
                <span className="flex items-center gap-1 text-blue-600" title={isUSA ? 'Accepts scheduling' : 'Acepta programaciones'}>
                  <CalendarCheck className="w-3 h-3" />
                  {isUSA ? 'Scheduling' : 'Programación'}
                </span>
              )}
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-slate-300" />
        </div>
      </CardContent>
    </Card>
  </Link>
);

export default ProvidersPage;
